import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import { FunnelSchema, SlaStatsSchema, type Funnel, type SlaStats } from "@/api/types/analytics";

/** Query-key factory — mirrors useDashboard's convention. */
export const analyticsKeys = {
  all: ["analytics"] as const,
  funnel: (since?: string) => [...analyticsKeys.all, "funnel", since ?? null] as const,
  sla: (since?: string) => [...analyticsKeys.all, "sla", since ?? null] as const,
};

/** GET /v2/analytics/funnel?since= — lead-to-cash stage counts,
 * stage-to-stage conversion rates, and the open-pipeline cash forecast. */
export function useFunnel(since?: string) {
  return useQuery<Funnel>({
    queryKey: analyticsKeys.funnel(since),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/analytics/funnel", {
        params: since ? { since } : undefined,
      });
      return validateResponse(FunnelSchema, data, "GET /v2/analytics/funnel");
    },
    staleTime: 30 * 1000,
  });
}

/** GET /v2/analytics/sla?since= — response-time distribution against the
 * 2h reply SLA. */
export function useSla(since?: string) {
  return useQuery<SlaStats>({
    queryKey: analyticsKeys.sla(since),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/analytics/sla", {
        params: since ? { since } : undefined,
      });
      return validateResponse(SlaStatsSchema, data, "GET /v2/analytics/sla");
    },
    staleTime: 30 * 1000,
  });
}
